import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { useAuth } from "@/hooks/useAuth";
import { CurrencyPreferenceProvider } from "@/hooks/useCurrencyPreference";
import Landing from "./pages/Landing";
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const AllExpenses = lazy(() => import("./pages/AllExpenses"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const GroupDetail = lazy(() => import("./pages/GroupDetail"));
const UngroupedExpenses = lazy(() => import("./pages/UngroupedExpenses"));
const NotFound = lazy(() => import("./pages/NotFound"));
const IconList = lazy(() => import("./pages/IconList"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));

const SubscriptionGate = lazy(() =>
  import("./components/subscription/SubscriptionGate").then((m) => ({ default: m.SubscriptionGate }))
);
const PushMessageListener = lazy(() =>
  import("./components/PushMessageListener").then((m) => ({ default: m.PushMessageListener }))
);
const Toaster = lazy(() => import("@/components/ui/toaster").then((m) => ({ default: m.Toaster })));
const Sonner = lazy(() => import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })));

const RouteFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-pulse text-muted-foreground">Loading...</div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60,
      retry: 1,
    },
  },
});

// Wraps the authenticated app section with heavy providers that the
// marketing landing page does not need (improves landing-page LCP).
function AppShell({ children }: { children: React.ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <CurrencyPreferenceProvider>
        <TooltipProvider>
          <Suspense fallback={null}>
            <PushMessageListener />
          </Suspense>
          {children}
        </TooltipProvider>
      </CurrencyPreferenceProvider>
    </QueryClientProvider>
  );
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <AppShell>
      <SubscriptionGate>{children}</SubscriptionGate>
    </AppShell>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.querySelectorAll('.overflow-auto').forEach((el) => {
      el.scrollTop = 0;
    });
  }, [pathname]);
  return null;
}

// Toggle the desktop-frame styling on <body> only for /app routes
function AppFrameToggle() {
  const { pathname } = useLocation();
  useEffect(() => {
    const isAppRoute = pathname === "/app" || pathname.startsWith("/app/");
    if (isAppRoute) {
      document.body.classList.add("app-desktop-frame");
    } else {
      document.body.classList.remove("app-desktop-frame");
    }
    return () => {
      document.body.classList.remove("app-desktop-frame");
    };
  }, [pathname]);
  return null;
}

const App = () => (
  <BrowserRouter>
    <ScrollToTop />
    <AppFrameToggle />
    {/* Toast portals are global but lazy so they don't block first paint */}
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
    </Suspense>
    <Suspense fallback={<RouteFallback />}>
      <Routes>
        {/* Public routes — render without heavy providers for fast LCP */}
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/invite/:code" element={<AcceptInvite />} />
        <Route path="/icons" element={<IconList />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        

        {/* App routes (protected) */}
        <Route
          path="/app"
          element={
            <ProtectedRoute>
              <Index />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/expenses"
          element={
            <ProtectedRoute>
              <AllExpenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/groups/:id"
          element={
            <ProtectedRoute>
              <GroupDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="/app/ungrouped"
          element={
            <ProtectedRoute>
              <UngroupedExpenses />
            </ProtectedRoute>
          }
        />
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  </BrowserRouter>
);

export default App;
